import type { StatsWindow, WindowStats } from './service.js'

// The shared PRESENTATION spec for moderation stats: the lookback windows + the display-bucket table +
// the remainder math BOTH renderers draw from — the in-chat /stats summary (bot/features/stats) and the
// Mini App stats view (web/frontend/stats.ts). One table, one remainder rule, so the two surfaces can
// never disagree on what a bucket means or on what "other" hides. Lives in services/stats/ beside the
// (presentation-free) service for the same reason settings/spec.ts lives in services/settings/: both
// frontends import it, and the web layer may never import the bot layer (docs/architecture.md §Boundary).
// Pure data + pure math — no prisma/pino/grammY, only TYPE imports — so the browser bundle can
// value-import this leaf module (the settings SECTIONS precedent; type imports are erased by esbuild).

// The two windows shown side by side ("recent (month)"). The SERVICE is window-agnostic; these are the
// product's editorial choice. `days` drives the audit_log `createdAt >= now - days` filter. The web
// caption (ui-stats-caption) states these numbers in static prose — labels.gen.test.ts pins the wording
// to THESE values, so changing a window without updating the caption fails the build (no lying caption).
export const STATS_RECENT: StatsWindow = { key: 'recent', days: 7 }
export const STATS_MONTH: StatsWindow = { key: 'month', days: 30 }
export const STATS_WINDOWS: readonly [StatsWindow, StatsWindow] = [STATS_RECENT, STATS_MONTH]

export interface StatsBucket {
  /** Language-neutral row glyph (the bot line's prefix; the web row's leading glyph). */
  emoji: string
  /** i18n key for the row label — a Fluent key for the bot, the same key via labels.gen for the web. */
  labelKey: string
  /** The audit `action` values this row counts. `action` is the CLOSED vocabulary (DOSSIER §9). */
  actions: readonly string[]
}

// Display buckets: each maps a set of audit `action` values to one localized row. Ordered by theme
// (manual moderation → automated removals → lockdowns → config). `reason` is free-form so we NEVER key
// off it. Any action NOT claimed by a bucket falls into the "other" remainder (bucketWindow), so nothing
// is silently lost from the picture (CLAUDE.md #6 diagnosable). An action must appear in AT MOST one
// bucket — spec.test.ts enforces it, because a double-claimed action would double-count and could push
// the remainder negative.
export const STATS_BUCKETS: readonly StatsBucket[] = [
  { emoji: '⚠️', labelKey: 'stats-row-warn', actions: ['warn'] },
  { emoji: '🔇', labelKey: 'stats-row-mute', actions: ['mute'] },
  { emoji: '🔨', labelKey: 'stats-row-ban', actions: ['ban'] },
  { emoji: '👢', labelKey: 'stats-row-kick', actions: ['kick'] },
  { emoji: '🌊', labelKey: 'stats-row-flood', actions: ['flood-delete'] },
  { emoji: '🛡️', labelKey: 'stats-row-filter', actions: ['filter-delete'] },
  { emoji: '🌙', labelKey: 'stats-row-lockdown', actions: ['night-engaged', 'raid-lockdown-engaged'] },
  { emoji: '⚙️', labelKey: 'stats-row-config', actions: ['settings.update'] },
]

// Audit actions that are NOT moderation activity and must be EXCLUDED from the /stats + Mini App activity
// counts. AI Shield's shadow-mode `ai-flag` rows record what the bot WOULD have flagged while taking NO
// action, so counting them — even in the "other" remainder — would report phantom moderation that never
// happened (a green-that-lies, contradicting shadow mode's no-action contract). getChatStats filters these
// at the query so they never reach `total` or `other`. Kept here beside STATS_BUCKETS as the one place the
// activity vocabulary lives (browser-safe leaf — type-free string constants only).
export const NON_ACTIVITY_ACTIONS = ['ai-flag'] as const

/** Sum a window's counts over a set of actions (an absent key means zero — groupBy omits empty groups). */
export function sumActions(counts: Record<string, number>, actions: readonly string[]): number {
  return actions.reduce((n, a) => n + (counts[a] ?? 0), 0)
}

// Every action a named bucket claims — the remainder subtracts exactly these, once each.
const NAMED_ACTIONS: readonly string[] = STATS_BUCKETS.flatMap(b => b.actions)

/**
 * One window bucketed for display: `buckets[i]` aligns with `STATS_BUCKETS[i]`; `other` is the window
 * total minus everything the named buckets claimed — the safety net that keeps a new or uncategorised
 * audit action visible instead of silently dropped. Whether a zero `other` row is SHOWN stays each
 * renderer's choice; the math lives here so it exists exactly once.
 */
export function bucketWindow(w: WindowStats): { buckets: number[], other: number } {
  return {
    buckets: STATS_BUCKETS.map(b => sumActions(w.counts, b.actions)),
    other: w.total - sumActions(w.counts, NAMED_ACTIONS),
  }
}
